import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Button, Popover, Select, Tag, Tooltip, message } from "antd";
import { EditOutlined } from "@ant-design/icons";

import { getLeadTags, getPracticeLead, setPracticeLeadTags } from "./controller";
import "./dormant.css";

/**
 * Lead tags — the shared vocabulary between the Leads page, the Practices
 * table and the drawers, so every surface edits the same set the same way.
 *
 * Tags are super-admin-defined and global; a lead carries a subset of them.
 * The picker works in *ids* (what the server stores) but a super admin can
 * also type a brand-new name inline. Because `value` only carries ids, the
 * names that do not exist yet live in the picker's own state until the parent
 * saves and the tag list reloads — at which point the name has an id and the
 * pending copy is dropped, so a saved tag never shows twice.
 */

// ── Data ───────────────────────────────────────────────────────────────────
export function useLeadTags() {
  const [tags, setTags] = useState([]);
  const [loading, setLoading] = useState(false);

  const reload = useCallback(async () => {
    setLoading(true);
    try {
      const res = await getLeadTags();
      // A failed fetch degrades to "no tags to choose from" rather than a crash —
      // the picker still lets a typed name through.
      setTags(res.status >= 200 && res.status < 300 && Array.isArray(res.json) ? res.json : []);
    } catch (e) {
      setTags([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { reload(); }, [reload]);

  return { tags, loading, reload };
}

const norm = (s) => String(s || "").trim().toLowerCase();

// ── Chips ──────────────────────────────────────────────────────────────────
// Colored tags, capped at `max` with a "+N" that lists the rest on hover.
export function TagChips({ tags, max = 3 }) {
  const list = Array.isArray(tags) ? tags : [];
  if (!list.length) return <span className="crm-sub">—</span>;
  const shown = list.slice(0, max);
  const rest = list.slice(max);
  return (
    <span className="crmd-chips">
      {shown.map((t) => (
        <Tag key={t.id || t.name} color={t.color || undefined} className="crmd-chip" title={t.description || undefined}>
          {t.name}
        </Tag>
      ))}
      {rest.length ? (
        <Tooltip title={rest.map((t) => t.name).join(", ")}>
          <Tag className="crmd-chip crmd-chip-more">+{rest.length}</Tag>
        </Tooltip>
      ) : null}
    </span>
  );
}

// ── Picker ─────────────────────────────────────────────────────────────────
export default function LeadTagPicker({
  value,
  onChange,
  allowCreate = true,
  size = "middle",
  placeholder,
  style,
}) {
  const { tags, loading } = useLeadTags();
  const ids = useMemo(() => (Array.isArray(value) ? value : []), [value]);
  // Names typed in this session that match no existing tag yet (see file note).
  const [pending, setPending] = useState([]);

  const byId = useMemo(() => {
    const m = {};
    tags.forEach((t) => { m[t.id] = t; });
    return m;
  }, [tags]);

  // Once a pending name exists as a real tag AND the parent now holds its id,
  // the name has been saved — drop the duplicate.
  useEffect(() => {
    setPending((prev) =>
      prev.filter((name) => {
        const hit = tags.find((t) => norm(t.name) === norm(name));
        return !(hit && ids.indexOf(hit.id) !== -1);
      })
    );
  }, [tags, ids]);

  const handleChange = (vals) => {
    const tagIds = [];
    const newTags = [];
    (vals || []).forEach((v) => {
      if (typeof v === "number") {
        if (tagIds.indexOf(v) === -1) tagIds.push(v);
        return;
      }
      const name = String(v).trim();
      if (!name) return;
      // A typed name that already exists (any case) is that tag, not a new one.
      const hit = tags.find((t) => norm(t.name) === norm(name));
      if (hit) {
        if (tagIds.indexOf(hit.id) === -1) tagIds.push(hit.id);
      } else if (allowCreate && !newTags.some((n) => norm(n) === norm(name))) {
        newTags.push(name);
      }
    });
    setPending(newTags);
    onChange && onChange({ tagIds, newTags });
  };

  const options = tags.map((t) => ({ value: t.id, label: t.name, color: t.color, description: t.description }));

  return (
    <Select
      // "multiple" cannot mint values, which is exactly how free entry is blocked.
      mode={allowCreate ? "tags" : "multiple"}
      size={size}
      style={Object.assign({ minWidth: 220, width: "100%" }, style)}
      placeholder={placeholder || (allowCreate ? "Pick tags or type a new one" : "Pick tags")}
      loading={loading}
      value={[].concat(ids, pending)}
      onChange={handleChange}
      options={options}
      optionFilterProp="label"
      tokenSeparators={[","]}
      // Popups inside the dormant wizard (a Modal at 10050) must clear it; a
      // higher layer is harmless everywhere else.
      dropdownStyle={{ zIndex: 10060 }}
      optionRender={(opt) => (
        <span className="crmd-option">
          <Tag color={opt.data.color || undefined} className="crmd-chip">{opt.data.label}</Tag>
          {opt.data.description ? <span className="crm-sub">{opt.data.description}</span> : null}
        </span>
      )}
      tagRender={(props) => {
        const { value: v, closable, onClose } = props;
        const t = typeof v === "number" ? byId[v] : null;
        return (
          <Tag
            color={t ? t.color || undefined : undefined}
            className={`crmd-chip${t ? "" : " crmd-chip-new"}`}
            closable={closable}
            onClose={onClose}
            onMouseDown={(e) => { e.preventDefault(); e.stopPropagation(); }}
          >
            {t ? t.name : v}
            {t ? null : <span className="crmd-chip-newmark">new</span>}
          </Tag>
        );
      }}
    />
  );
}

// ── Practice-side editor ───────────────────────────────────────────────────
/**
 * Chips + a pencil popover to edit the tags of a PRACTICE's lead.
 *
 * Two data modes, chosen by the caller:
 *   - `tags` given: render them as-is (the Practices table already has them on
 *     the row, and a request per row would blow the page's query budget).
 *   - `tags` omitted: fetch the lead lazily on mount (the drawers, where one
 *     request per open drawer is fine and the lead may not exist yet — the
 *     server creates it on the spot).
 * Saving replaces the lead's tag set and hands the parent the new chips.
 */
export function PracticeTagEditor({ practiceId, tags, onSaved, max = 3, size = "small" }) {
  const lazy = tags === undefined;
  const [own, setOwn] = useState(Array.isArray(tags) ? tags : []);
  const [loadingLead, setLoadingLead] = useState(false);
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState(null); // {tagIds, newTags} while editing
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!lazy) setOwn(Array.isArray(tags) ? tags : []);
  }, [tags, lazy]);

  useEffect(() => {
    if (!lazy || !practiceId) return;
    let alive = true;
    setLoadingLead(true);
    getPracticeLead(practiceId)
      .then((res) => {
        if (!alive) return;
        if (res.status >= 200 && res.status < 300 && res.json) {
          setOwn(Array.isArray(res.json.tags) ? res.json.tags : []);
        } else {
          message.error((res.json && res.json.detail) || "Couldn't load the practice's lead.");
        }
      })
      .catch(() => {})
      .finally(() => { if (alive) setLoadingLead(false); });
    return () => { alive = false; };
  }, [lazy, practiceId]);

  const current = draft || { tagIds: own.map((t) => t.id), newTags: [] };

  const save = async () => {
    setBusy(true);
    const res = await setPracticeLeadTags(practiceId, current);
    setBusy(false);
    if (res.status >= 200 && res.status < 300 && res.json) {
      const next = Array.isArray(res.json.tags) ? res.json.tags : [];
      setOwn(next);
      setDraft(null);
      setOpen(false);
      onSaved && onSaved(next);
    } else {
      message.error((res.json && res.json.detail) || "Couldn't save the tags.");
    }
  };

  const content = (
    <div className="crmd-tag-pop" onClick={(e) => e.stopPropagation()}>
      <LeadTagPicker value={current.tagIds} onChange={setDraft} size="middle" style={{ width: 300 }} />
      <div className="crmd-tag-pop-foot">
        <Button size="small" onClick={() => { setDraft(null); setOpen(false); }}>Cancel</Button>
        <Button size="small" type="primary" loading={busy} onClick={save} disabled={!draft}>Save</Button>
      </div>
    </div>
  );

  return (
    <span className="crmd-tag-cell" onClick={(e) => e.stopPropagation()}>
      {loadingLead ? <span className="crm-sub">Loading…</span> : <TagChips tags={own} max={max} />}
      <Popover
        trigger="click"
        open={open}
        onOpenChange={(o) => { setOpen(o); if (!o) setDraft(null); }}
        content={content}
        placement="bottomLeft"
        destroyTooltipOnHide
      >
        <Button
          size={size}
          type="text"
          icon={<EditOutlined />}
          className="crmd-tag-edit"
          aria-label="Edit tags"
          disabled={!practiceId || loadingLead}
        />
      </Popover>
    </span>
  );
}
